/* eslint-disable react-hooks/exhaustive-deps */
import './index.less';

import {
  useColorMode,
  ToastManager,
  TonicProvider,
  colorStyle,
} from '@tonic-one/react';
import { lazy, Suspense, useEffect } from 'react';
import { CookiesProvider } from 'react-cookie';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import merge from 'lodash/merge';

import PrivateApp from './App';
import AntdThemeProvider from './AntdThemeProvider';
import Toaster from './components/Toaster';
import ClpUpdate from './pages/Public/ClpUpdate';
import ClpRedirect from './pages/Public/ClpRedirect';
import Provision from './pages/Public/Provision';
import AdminConsent from './pages/Public/AdminConsent';
import ServiceException from '@Components/Common/ServiceException';
import { Login } from './pages/Login';
import { useLicenseStore } from '@Models/useLicenseModel';
import { DcSite } from './pages/DcSite';
import { isDefaultUI, isInUIC } from '@Utils/common';
import { SsoLogin } from './pages/Login/SsoLogin';
import { HoxRoot } from 'hox';
import config from './config';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './QueryClient';

window.onerror = function (message, source, lineno, colno, error) {
  console.error(message, source, lineno, colno, error);
};

const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((module) => ({
    default: module.ReactQueryDevtools,
  }))
);

function setUpContextMenu() {
  const script = document.createElement('script');
  script.src = '/internal/uc/contextmenu/v1/actions';
  script.onload = () => {
    if (!window.xdrContextMenu) {
      console.error(
        'load contextmenu actions script done, but xdrContextMenu cannot be found'
      );
    }
  };
  script.onerror = () => {
    console.error('Failed to load contextmenu actions script');
  };
  document.head.append(script);
}

export function Index() {
  const customColorStyle = {
    dark: {
      background: {
        fourth: 'gray:70',
      },
    },
    light: {
      background: {
        fourth: 'gray:30',
      },
    },
  };

  useEffect(() => {
    if (window.location.hash) {
      const rawPathInput = window.location.hash.substring(3);

      try {
        // Use the URL constructor for safe parsing and validation.
        // It resolves the input path relative to the current origin.
        const currentOrigin = window.location.origin;
        const targetUrl = new URL(rawPathInput, currentOrigin);

        // 1. Ensure the resolved URL is still on the SAME ORIGIN.
        if (targetUrl.origin !== currentOrigin) {
          return; // Abort
        }

        // 2. Ensure the protocol is safe (http or https).
        //    The URL constructor often blocks 'javascript:' when base is http/https,
        //    but an explicit check adds robustness.
        if (targetUrl.protocol !== 'http:' && targetUrl.protocol !== 'https:') {
          return; // Abort
        }

        // --- Perform Safe Redirect ---
        const safeRedirectPath = targetUrl.pathname + targetUrl.search; // Include query params if any
        console.log(`Redirecting to safe path: ${safeRedirectPath}`);
        window.location.href = safeRedirectPath; // Perform the same-origin redirect
      } catch (e) {
        console.error('Error parsing redirect path from hash:', e);
      }
    }
  }, []);

  const customColor = merge(colorStyle, customColorStyle);

  useEffect(() => {
    if (isInUIC()) {
      setUpContextMenu();
    }
  }, []);
  return (
    <Suspense fallback="loading">
      <QueryClientProvider client={queryClient}>
        <HoxRoot>
          <CookiesProvider>
            <TonicProvider
              colorStyle={{ defaultValue: customColor }}
              useCSSBaseline={true}
              colorMode={{
                defaultValue: 'dark',
              }}
            >
              <ToastManager>
                <AntdThemeProvider>
                  <ColorSetUp />
                  <Toaster />
                  <BrowserRouter basename={config.ui.prefix}>
                    <Routes>
                      <Route path="/provision.html" element={<Provision />} />
                      <Route
                        path="/admin-consent.html"
                        element={<AdminConsent />}
                      />
                      <Route path="/clp-update" element={<ClpUpdate />} />
                      <Route path="/clp-redirect" element={<ClpRedirect />} />
                      {isDefaultUI() && (
                        <>
                          <Route path="/login" element={<Login />} />
                          <Route path="/init_config" element={<DcSite />} />
                          <Route path="/forgot" element={<Login />} />
                          <Route
                            path="/ui_saml_sso_login"
                            element={<SsoLogin />}
                          />
                        </>
                      )}
                      <Route
                        path="/service-exception"
                        element={<ServiceException />}
                      />
                      <Route path="*" element={<PrivateApp />} />
                    </Routes>
                  </BrowserRouter>
                </AntdThemeProvider>
              </ToastManager>
            </TonicProvider>
          </CookiesProvider>
        </HoxRoot>
        {import.meta.env.MODE === 'development' && (
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="bottom-left"
          />
        )}
      </QueryClientProvider>
    </Suspense>
  );
}

const ColorSetUp = () => {
  const [, setColorMode] = useColorMode();
  const isV1App = isInUIC();
  const { license } = useLicenseStore();
  useEffect(() => {
    if (isV1App) {
      // When embedded in UIC, the theme follows the host UIC app, which stores
      // the active theme in localStorage under the `uic_theme` key. Only
      // `dark` and `light` are valid; unknown/missing values fall back to dark
      // (the previous UIC default).
      const applyUicTheme = () => {
        const uicTheme = localStorage.getItem('uic_theme');
        setColorMode(uicTheme === 'light' ? 'light' : 'dark');
      };
      applyUicTheme();
      const onStorage = (event: StorageEvent) => {
        if (event.key === 'uic_theme') {
          applyUicTheme();
        }
      };
      window.addEventListener('storage', onStorage);
      return () => {
        window.removeEventListener('storage', onStorage);
      };
    }
    const isBlackMode = localStorage.getItem('isBlackMode') || '';
    if (isBlackMode === '') {
      if (
        license &&
        license.tenantId &&
        license.tenantId.toLowerCase() === 'tm'
      ) {
        setColorMode('dark');
      } else {
        setColorMode('light');
      }
    } else {
      setColorMode(isBlackMode === 'false' ? 'light' : 'dark');
    }
  }, [license.tenantId]);
  return <></>;
};

async function enableMocking() {
  if (!import.meta.env.DEV) {
    return;
  }

  const appEnv = import.meta.env.VITE_APP_ENV || 'production';

  if (appEnv !== 'mock') {
    console.log(`Environment is '${appEnv}', skipping MSW`);
    return;
  }

  console.log('import.meta.env:', import.meta.env);
  try {
    const { worker } = await import('./mocks/browser');

    const result = await worker.start({
      onUnhandledRequest: 'bypass',
    });

    console.log('MSW worker.start() result:', result);
  } catch (error) {
    console.error('MSW failed to start with error:', error);
    console.error(
      'Error stack:',
      error instanceof Error ? error.stack : 'No stack'
    );
  }
}

const container = document.getElementById('root');
const root = createRoot(container as HTMLElement);

enableMocking()
  .then(() => {
    root.render(<Index />);
  })
  .catch((error) => {
    console.error('enableMocking() failed with error:', error);
    root.render(<Index />);
  });
